<template>
  <div class="login-page d-flex align-items-center justify-content-center">
    <div class="login-bg" aria-hidden="true"></div>
    <div class="login-ripples" aria-hidden="true"></div>
    <div class="login-geo" aria-hidden="true">
      <span class="login-geo-bar"></span>
    </div>

    <div class="login-card">
      <h1 class="page-title">线上考试系统</h1>
      <p class="page-subtitle">欢迎登录，开启高效智能考试体验</p>

      <form @submit.prevent="handleSubmit" novalidate>
        <div class="mb-3">
          <label class="form-label">账号</label>
          <input
            v-model.trim="form.username"
            type="text"
            class="form-control custom-input"
            placeholder="请输入账号或手机号"
          />
          <small v-if="errors.username" class="error-text">{{ errors.username }}</small>
        </div>

        <div class="mb-3">
          <label class="form-label">密码</label>
          <input
            v-model.trim="form.password"
            type="password"
            class="form-control custom-input"
            placeholder="请输入密码"
          />
          <small v-if="errors.password" class="error-text">{{ errors.password }}</small>
        </div>

        <div class="mb-3">
          <label class="form-label">验证码</label>
          <div class="d-flex gap-2">
            <input
              v-model.trim="form.captcha"
              type="text"
              class="form-control custom-input"
              placeholder="请输入验证码"
            />
            <button type="button" class="btn captcha-btn" @click="generateCaptcha">
              {{ generatedCaptcha }}
            </button>
          </div>
          <small v-if="errors.captcha" class="error-text">{{ errors.captcha }}</small>
        </div>

        <button type="submit" class="btn login-btn w-100 mt-2">登录</button>
      </form>

      <div class="register-link mt-3">
        <span class="register-link-muted">还没有账号？</span>
        <a class="register-link-action" href="javascript:void(0)" @click.prevent="goRegister">去注册</a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoginView',
  data() {
    return {
      form: {
        username: '',
        password: '',
        captcha: ''
      },
      generatedCaptcha: '',
      errors: {
        username: '',
        password: '',
        captcha: ''
      }
    }
  },
  created() {
    this.generateCaptcha()
  },
  methods: {
    goRegister() {
      window.location.hash = '#/register'
    },
    generateCaptcha() {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
      let value = ''
      for (let i = 0; i < 6; i += 1) {
        value += chars.charAt(Math.floor(Math.random() * chars.length))
      }
      this.generatedCaptcha = value
    },
    validate() {
      // 兼容本系统(6-10)与 Automan(8-16)，具体账号类型由后端自动判断
      const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,16}$/
      const captchaRegex = /^[A-Za-z\d]{6}$/

      this.errors.username = ''
      this.errors.password = ''
      this.errors.captcha = ''

      if (!this.form.username || this.form.username.length < 3) {
        this.errors.username = '账号不能为空，且长度至少3位'
      }

      if (!passwordRegex.test(this.form.password)) {
        this.errors.password = '密码需为6-16位且包含数字和字母'
      }

      if (!captchaRegex.test(this.form.captcha)) {
        this.errors.captcha = '验证码需为6位数字和字母组合'
      } else if (
        this.form.captcha.toLowerCase() !== this.generatedCaptcha.toLowerCase()
      ) {
        this.errors.captcha = '验证码错误，请重新输入'
      }

      return !this.errors.username && !this.errors.password && !this.errors.captcha
    },
    applyLoginSuccess(user) {
      const role = String(user.role || '').trim().toLowerCase()
      localStorage.setItem('exam_user', JSON.stringify(user))
      window.dispatchEvent(new Event('auth-changed'))
      let targetHash = '#/student/home'
      if (role === 'admin') {
        targetHash = '#/home/city-signup'
      } else if (role === 'teacher') {
        targetHash = '#/teacher/questions'
      } else if (role === 'student') {
        targetHash = '#/student/home'
      } else {
        window.alert('用户角色异常，无法进入系统')
        localStorage.removeItem('exam_user')
        window.dispatchEvent(new Event('auth-changed'))
        return
      }
      window.dispatchEvent(new Event('exam-login-success-toast'))
      if (window.location.hash === targetHash) {
        window.dispatchEvent(new Event('auth-changed'))
      } else {
        window.location.hash = targetHash
      }
    },
    handleSubmit() {
      if (!this.validate()) {
        return
      }
      fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': '1'
        },
        body: JSON.stringify({
          username: this.form.username,
          password: this.form.password
        })
      })
        .then((res) => res.json())
        .then((res) => {
          if (res.code === 200) {
            this.applyLoginSuccess(res.data || {})
            return
          }
          window.alert(res.message || res.msg || '登录失败')
          this.generateCaptcha()
        })
        .catch(() => {
          window.alert('网络异常，请检查后端服务是否已启动')
        })
    }
  }
}
</script>

<style scoped>
/* 校园 / 企业内部 · 纯净白 + 浅天蓝 · 简约几何 + 轻微波纹（仅样式） */
.login-page {
  --sky-50: #f0f9ff;
  --sky-100: #e0f2fe;
  --sky-200: #bae6fd;
  --sky-500: #0ea5e9;
  --sky-600: #0284c7;
  --slate-500: #64748b;
  --slate-600: #475569;
  --slate-800: #1e293b;
  --type-title: clamp(1.375rem, 3.5vw, 1.625rem);
  --type-subtitle: 0.9375rem;
  --type-label: 0.875rem;
  --type-input: 1rem;
  --type-foot: 0.875rem;

  position: relative;
  isolation: isolate;
  min-height: 100vh;
  padding: clamp(20px, 5vw, 48px);
  overflow: hidden;
  background: #f8fafc;
  font-family:
    'Segoe UI',
    'PingFang SC',
    'Microsoft YaHei',
    system-ui,
    sans-serif;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  font-size: 16px;
}

.login-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse 90% 55% at 50% -8%, rgba(186, 230, 253, 0.55), transparent 58%),
    radial-gradient(ellipse 55% 42% at 100% 100%, rgba(224, 242, 254, 0.9), transparent 52%),
    radial-gradient(ellipse 50% 38% at 0% 100%, rgba(240, 249, 255, 0.95), transparent 50%),
    linear-gradient(180deg, #ffffff 0%, var(--sky-50) 42%, #f8fafc 100%);
}

.login-ripples {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  opacity: 0.35;
  background: repeating-radial-gradient(
    circle at 50% 108%,
    transparent 0,
    transparent 52px,
    rgba(14, 165, 233, 0.045) 52px,
    rgba(14, 165, 233, 0.045) 53px
  );
  mask-image: radial-gradient(ellipse 85% 70% at 50% 55%, black 0%, transparent 72%);
}

.login-geo {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
}

.login-geo-bar {
  position: absolute;
  bottom: 12%;
  left: 4%;
  width: 4px;
  height: min(120px, 22vh);
  border-radius: 4px;
  background: linear-gradient(180deg, rgba(14, 165, 233, 0.2) 0%, rgba(14, 165, 233, 0.06) 100%);
}

.login-card {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 420px;
  padding: 40px 36px 36px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid rgba(186, 230, 253, 0.65);
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 12px 40px -8px rgba(14, 165, 233, 0.12),
    0 24px 48px -16px rgba(15, 23, 42, 0.06);
}

.login-card > form,
.login-card > h1,
.login-card > p,
.login-card > .register-link {
  position: relative;
  z-index: 1;
}

.page-title {
  margin: 0;
  text-align: center;
  font-size: var(--type-title);
  font-weight: 700;
  letter-spacing: 0.12em;
  line-height: 1.35;
  color: var(--slate-800);
}

.page-subtitle {
  margin: 12px auto 28px;
  max-width: 24em;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--sky-100);
  text-align: center;
  color: var(--slate-600);
  font-size: var(--type-subtitle);
  font-weight: 500;
  letter-spacing: 0.03em;
  line-height: 1.65;
}

.form-label {
  display: block;
  color: var(--slate-800);
  margin-bottom: 8px;
  font-size: var(--type-label);
  font-weight: 600;
  letter-spacing: 0.06em;
  line-height: 1.4;
}

.login-page .login-card .form-control.custom-input {
  color: var(--slate-800) !important;
  caret-color: var(--sky-600);
}

.login-page .login-card .form-control.custom-input::placeholder,
.login-page .login-card .form-control.custom-input::-webkit-input-placeholder,
.login-page .login-card .form-control.custom-input::-moz-placeholder {
  color: var(--slate-500) !important;
  opacity: 1 !important;
}

.login-page .login-card .form-control.custom-input:-webkit-autofill,
.login-page .login-card .form-control.custom-input:-webkit-autofill:hover,
.login-page .login-card .form-control.custom-input:-webkit-autofill:focus {
  -webkit-text-fill-color: var(--slate-800) !important;
  box-shadow: 0 0 0 1000px #f8fafc inset !important;
  transition: background-color 99999s ease-out;
}

.custom-input {
  border-radius: 10px;
  border: 1px solid var(--sky-200);
  background: #ffffff;
  color: var(--slate-800);
  font-size: var(--type-input);
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0.02em;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.custom-input::placeholder {
  color: var(--slate-500);
  font-weight: 400;
}

.login-page .login-card .form-control.custom-input:focus {
  color: var(--slate-800) !important;
}

.custom-input:focus {
  border-color: var(--sky-500);
  background: #ffffff;
  box-shadow:
    0 0 0 3px rgba(14, 165, 233, 0.15),
    0 1px 2px rgba(15, 23, 42, 0.05);
  outline: none;
}

.captcha-btn {
  min-width: 118px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff !important;
  color: var(--sky-600) !important;
  -webkit-text-fill-color: var(--sky-600);
  font-weight: 900;
  font-size: 1.125rem;
  letter-spacing: 0.12em;
  font-variant-numeric: tabular-nums;
  font-family: 'SimHei', 'STHeiti', 'Heiti SC', 'Microsoft YaHei', sans-serif;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.captcha-btn:hover {
  border-color: var(--sky-500);
  background: #ffffff !important;
  color: #0369a1 !important;
  -webkit-text-fill-color: #0369a1;
  box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.12);
}

.login-btn {
  border-radius: 10px;
  border: none;
  background: linear-gradient(180deg, var(--sky-500) 0%, var(--sky-600) 100%);
  color: #ffffff !important;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  line-height: 1.45;
  padding: 12px 0;
  box-shadow: 0 4px 14px rgba(2, 132, 199, 0.28);
  transition: transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease;
}

.login-btn:hover {
  filter: brightness(1.03);
  box-shadow: 0 6px 20px rgba(2, 132, 199, 0.32);
  transform: translateY(-1px);
}

.login-btn:active {
  transform: translateY(0);
}

.error-text {
  color: #dc2626;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.45;
  letter-spacing: 0.02em;
}

.register-link {
  text-align: center;
  font-size: var(--type-foot);
  line-height: 1.6;
  letter-spacing: 0.02em;
}

.login-page .login-card .register-link-muted {
  color: var(--slate-500) !important;
  font-weight: 500;
  margin-right: 0.25em;
}

.login-page .login-card a.register-link-action {
  color: var(--sky-600) !important;
  text-decoration: none;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.login-page .login-card a.register-link-action:hover {
  color: #0369a1 !important;
  text-decoration: underline;
}

@media (prefers-reduced-motion: reduce) {
  .login-btn:hover,
  .login-btn:active,
  .captcha-btn:hover {
    transform: none;
  }
}
</style>
